import { useTranslation } from "react-i18next";
import React from 'react';
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="p-10">
      <Button
        variant="ghost"
        className="absolute top-4 left-4 text-[#F1F6F5] bg-[#8791F3] hover:text-[#34375B] hover:bg-[#5CC2BB] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200"
        onClick={() => navigate("/")}
      >
        <ArrowLeft />
        {t("hero.back")}
      </Button>

      <h1 className="text-5xl text-[#34375B]">Privacy Policy</h1>
      <p className="text-xl font-thin text-[#34375B]/80"><strong>Last updated:</strong> February 22, 2025</p>

      <div className="space-y-8 py-8 text-left">
        <section>
          <h2 className="font-bold text-2xl mb-4 text-[#34375B]">1. Information We Collect</h2>
          <p className="text-xl font-thin font-thin text-[#34375B]/80">At Billdrip, we collect and process different types of personal information, including:</p>
          <ul className="list-disc list-inside pb-[15px]">
            <li className="text-xl font-thin text-[#34375B]/80"><strong>Personal Information:</strong> Your name, email, and contact details.</li>
            <li className="text-xl font-thin text-[#34375B]/80"><strong>Business Information:</strong> Company name, industry, and other business-related details.</li>
            <li className="text-xl font-thin text-[#34375B]/80"><strong>Payment and Invoice Data:</strong> Billing details, payment methods, and invoices.</li>
            <li className="text-xl font-thin text-[#34375B]/80"><strong>Communication Preferences:</strong> Your preferences for marketing emails and notifications.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-2xl mb-4 text-[#34375B]">2. How We Use Your Information</h2>
          <p className="text-xl font-thin text-[#34375B]/80">We use the collected data for:</p>
          <ul className="list-disc list-inside ml-4">
            <li className="text-xl font-thin text-[#34375B]/80">Providing and improving our invoicing and payment scheduling services.</li>
            <li className="text-xl font-thin text-[#34375B]/80">Processing transactions and managing billing information.</li>
            <li className="text-xl font-thin text-[#34375B]/80">Communicating important updates regarding your account and services.</li>
            <li className="text-xl font-thin text-[#34375B]/80">Ensuring compliance with legal obligations and security standards.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-2xl mb-4 text-[#34375B]">3. How We Share Your Information</h2>
          <p className="text-xl font-thin text-[#34375B]/80">We do not sell your personal data. However, we may share information with:</p>
          <ul className="list-disc list-inside ml-4">
            <li className="text-xl font-thin text-[#34375B]/80"><strong>Service Providers:</strong> Payment processors, cloud hosting, and analytics providers.</li>
            <li className="text-xl font-thin text-[#34375B]/80"><strong>Legal Authorities:</strong> If required by law or legal requests.</li>
            <li className="text-xl font-thin text-[#34375B]/80"><strong>Business Transfers:</strong> In the event of a company merger or acquisition.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-bold text-2xl mb-4 text-[#34375B]">4. Data Retention and Security</h2>
          <p className="text-xl font-thin text-[#34375B]/80">We retain your information as long as necessary for our services and legal compliance. We implement strict security measures to protect your data from unauthorized access.</p>
        </section>

        <section>
          <h2 className="font-bold text-2xl mb-4 text-[#34375B]">5. Your Rights and Choices</h2>
          <p className="text-xl font-thin text-[#34375B]/80">You have rights regarding your personal data, including:</p>
          <ul className="list-disc list-inside ml-4">
            <li className="text-xl font-thin text-[#34375B]/80"><strong>Access:</strong> Request a copy of your data.</li>
            <li className="text-xl font-thin text-[#34375B]/80"><strong>Correction:</strong> Update or modify incorrect information.</li>
            <li className="text-xl font-thin text-[#34375B]/80"><strong>Deletion:</strong> Request removal of your personal data.</li>
            <li className="text-xl font-thin text-[#34375B]/80"><strong>Opt-Out:</strong> Unsubscribe from marketing communications.</li>
          </ul>
          <p className="text-xl font-thin text-[#34375B]/80">To exercise your rights, contact us at <a href="mailto:contact@billdrip.com" className="text-blue-600 hover:underline">contact@billdrip.com</a>.</p>
        </section>

        <section>
          <h2 className="font-bold text-2xl mb-4 text-[#34375B]">6. International Data Transfers</h2>
          <p className="text-xl font-thin text-[#34375B]/80">Your data may be processed outside of the European Economic Area (EEA). We ensure that all data transfers comply with applicable laws and provide necessary safeguards.</p>
        </section>

        <section>
          <h2 className="font-bold text-2xl mb-4 text-[#34375B]">7. Cookies and Tracking</h2>
          <p className="text-xl font-thin text-[#34375B]/80">We use cookies and similar technologies to enhance user experience. You can manage cookie preferences through your browser settings.</p>
        </section>

        <section>
          <h2 className="font-bold text-2xl mb-4 text-[#34375B]">8. Changes to This Privacy Policy</h2>
          <p className="text-xl font-thin text-[#34375B]/80">We may update this policy occasionally. The "Last Updated" date will reflect any changes. Please review this policy periodically.</p>
        </section>

        <section>
          <h2 className="font-bold text-2xl mb-4 text-[#34375B]">9. Contact Us</h2>
          <p className="text-xl font-thin text-[#34375B]/80">If you have any questions, feel free to reach out:</p>
          <ul className="list-disc list-inside ml-4">
            <li className="text-xl font-thin text-[#34375B]/80">Email: <a href="mailto:contact@billdrip.com" className="text-blue-600 hover:underline">contact@billdrip.com</a></li>
            <li className="text-xl font-thin text-[#34375B]/80">Address: <strong>R. Febo Moniz 27B, 1150-152 Lisboa</strong></li>
          </ul>
        </section>
      </div>
    </div>
  );
};
export default PrivacyPolicy;